import { useMemo } from 'react';
import useReportLog from '../../../hooks/useReportLog';
import useGroup from '../../../zustand/useGroup';
import useAppliedReportFilter from '../../../hooks/useAppliedReportFilter';

import styled from 'styled-components';
import { Divider, Dropdown, Grid, Spin, Table, TableProps } from 'antd';
import {
  convertIntervalToSecond,
  generateTimestampData,
  generateDataSeriesTable,
  toHHMM,
  EChartColor,
  toKnot,
  toKM,
  toNM,
  convertData,
  toMiles,
} from '../../report/components/helperFunction';
import ReportChart from '../../report/components/reportChart';
import { useVT } from 'virtualizedtableforantd4';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EReportDataType } from '../../report/types/report-log.type';
import usePermission from '../../../hooks/usePermission';
import ExportDataReport from '../../report/components/exportDataReport';
import FilterReport from '../../../components/FilterReport';
import { EFilterItem } from '../../masterReport/types/report.type';
import { DeviceProperties } from '../types/device.type';
import { speedUnitOptions } from '../../../helpers/unit';
import { CaretDownOutlined } from '@ant-design/icons';
import { TitleChild, Unit } from '../../report/components/tableReportLog';

interface IProps {
  height?: number;
  device: DeviceProperties;
  columns: any;
  schema: any;
  yAxis: string[];
}
dayjs.extend(utc);

const windowHeight = window.innerHeight;

const DeviceDataLog: React.FC<IProps> = ({
  height,
  device,
  columns,
  schema,
  yAxis,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const {
    logs,
    speedUnit,
    distanceUnit,
    setSpeedUnit,
    setDistanceUnit,
    loadingLog,
  } = useReportLog();
  const { isUserHasPermission } = usePermission();
  const { filter, timezone } = useAppliedReportFilter();
  const [vt] = useVT(() => ({ scroll: { x: 700, y: windowHeight - 300 } }), []);

  const showTemp = (
    data: any,
    dataType?: EReportDataType | null | undefined
  ) => {
    let color = 'black';
    if (
      dataType &&
      dataType === EReportDataType.temp &&
      isUserHasPermission(['IMPORT_DATA_LOG' + '.CREATE'])
    ) {
      color = '#195ae7';
    }

    return <span style={{ color }}>{data}</span>;
  };

  const handleChangeUnit = (e: any, unitType: string) => {
    if (unitType === 'speed') {
      setSpeedUnit(e.key);
    } else if (unitType === 'distance') {
      setDistanceUnit(e.key);
    }
  };

  const columnFiltered = useMemo(() => {
    return columns.map((item: any) => {
      if ((item.title as string).toLowerCase() === 'speed') {
        return {
          ...item,
          title: () => (
            <div>
              <span>{item.title}</span>
              <Dropdown
                menu={{
                  items: speedUnitOptions.map((item) => ({
                    key: item.value,
                    label: item.label,
                  })),
                  onClick: (e) => handleChangeUnit(e, 'speed'),
                }}
              >
                <TitleChild>
                  <Unit>({speedUnit})</Unit>
                  <CaretDownOutlined style={{ opacity: 0.5 }} />
                </TitleChild>
              </Dropdown>
            </div>
          ),
        };
      }

      return item;
    });
  }, [columns, speedUnit, distanceUnit]);

  const dataSource: any = useMemo(() => {
    if (logs && Object.keys(logs).length) {
      let intervalInSecond = convertIntervalToSecond(filter.interval);

      const containerTimestamp = generateTimestampData({
        startTime: filter.startAt,
        endTime: filter.endAt,
        interval: filter.interval,
        intervalInSecond,
        timezone,
      });

      const results: any[] = [];

      containerTimestamp.map((timestamp) => {
        if (logs.gps) {
          const foundKey = Object.keys(logs.gps).find(
            (key) => Number(key) === timestamp
          );

          if (foundKey) {
            const gps = logs.gps[foundKey];

            let gps_speed = undefined;
            let gps_distance = undefined;

            if (speedUnit === 'KNOT' && gps?.speed) {
              gps_speed = toKnot(gps.speed);
            } else {
              gps_speed = gps?.speed;
            }

            if (distanceUnit === 'KM' && gps?.stats?.sumDistance) {
              gps_distance = toKM(gps?.stats?.sumDistance);
            } else if (distanceUnit === 'NM' && gps?.stats?.sumDistance) {
              gps_distance = toNM(gps?.stats?.sumDistance);
            } else if (distanceUnit === 'MILES' && gps?.stats?.sumDistance) {
              gps_distance = toMiles(gps?.stats?.sumDistance);
            } else {
              gps_distance = gps?.stats?.sumDistance;
            }

            results.push({
              timestamp,
              dateTime: dayjs(timestamp * 1000)
                .utcOffset(timezone * 60)
                .format('DD MMM YYYY HH:mm'),
              gps_latitude: showTemp(gps?.latitude),
              gps_longitude: showTemp(gps?.longitude),
              gps_altitude: showTemp(gps?.altitude),
              gps_heading: showTemp(convertData(gps?.heading, true)),
              gps_speed: showTemp(convertData(gps_speed, true)),
              gps_distance: showTemp(convertData(gps_distance, true)),
            });
          }
        } else if (logs.rpm) {
          const foundKey = Object.keys(logs.rpm).find(
            (key) => Number(key) === timestamp
          );
          if (foundKey) {
            const rpm = logs.rpm[foundKey];
            results.push({
              timestamp,
              dateTime: dayjs(timestamp * 1000)
                .utcOffset(timezone * 60)
                .format('DD MMM YYYY HH:mm'),
              port_rpm: showTemp(
                convertData(rpm?.port?.rpm, true),
                rpm?.port?.dataType
              ),
              port_running_time: showTemp(
                rpm?.port?.runningTime,
                rpm?.port?.dataType
              ),
              starboard_rpm: showTemp(
                convertData(rpm?.starboard?.rpm, true),
                rpm?.starboard?.dataType
              ),
              starboard_running_time: showTemp(
                rpm?.starboard?.runningTime,
                rpm?.starboard?.dataType
              ),
              center_rpm: showTemp(
                convertData(rpm?.center?.rpm, true),
                rpm?.center?.dataType
              ),
              center_running_time: showTemp(
                rpm?.center?.runningTime,
                rpm?.center?.dataType
              ),
            });
          }
        } else if (logs.clutch) {
          const foundKey = Object.keys(logs.clutch).find(
            (key) => Number(key) === timestamp
          );
          if (foundKey) {
            const clutch = logs.clutch[foundKey];
            results.push({
              timestamp,
              dateTime: dayjs(timestamp * 1000)
                .utcOffset(timezone * 60)
                .format('DD MMM YYYY HH:mm'),
              port_clutch: showTemp(
                convertData(clutch?.port?.clutch, true),
                clutch?.port?.dataType
              ),
              port_running_time: showTemp(
                clutch?.port?.runningTime,
                clutch?.port?.dataType
              ),
              starboard_clutch: showTemp(
                convertData(clutch?.starboard?.clutch, true),
                clutch?.starboard?.dataType
              ),
              starboard_running_time: showTemp(
                clutch?.starboard?.runningTime,
                clutch?.starboard?.dataType
              ),
              center_clutch: showTemp(
                convertData(clutch?.center?.clutch, true),
                clutch?.center?.dataType
              ),
              center_running_time: showTemp(
                clutch?.center?.runningTime,
                clutch?.center?.dataType
              ),
            });
          }
        } else if (logs.flowmeter) {
          const foundKey = Object.keys(logs.flowmeter).find(
            (key) => Number(key) === timestamp
          );
          if (foundKey) {
            const fm = logs.flowmeter[foundKey];
            let currFm;

            if (device.uniqueId.includes('fm-1-1')) {
              currFm = fm.portIn;
            } else if (device.uniqueId.includes('fm-1-2')) {
              currFm = fm.portOut;
            } else if (device.uniqueId.includes('fm-2-1')) {
              currFm = fm.starboardIn;
            } else if (device.uniqueId.includes('fm-2-2')) {
              currFm = fm.starboardOut;
            } else if (device.uniqueId.includes('fm-3-1')) {
              currFm = fm.centerIn;
            } else if (device.uniqueId.includes('fm-3-2')) {
              currFm = fm.centerOut;
            }

            results.push({
              timestamp,
              dateTime: dayjs(timestamp * 1000)
                .utcOffset(timezone * 60)
                .format('DD MMM YYYY HH:mm'),
              volume_flowrate: showTemp(
                convertData(currFm?.volumeFlowrate, true),
                currFm?.dataType
              ),
              volume_inventory: showTemp(
                convertData(currFm?.volumeInventory, true),
                currFm?.dataType
              ),
              volume_total: showTemp(
                convertData(currFm?.volumeTotal, true),
                currFm?.dataType
              ),
              density: showTemp(
                convertData(currFm?.density, true),
                currFm?.dataType
              ),
              mass_flow_rate: showTemp(
                convertData(currFm?.massFlowrate, true),
                currFm?.dataType
              ),
              mass_inventory: showTemp(
                convertData(currFm?.massInventory, true),
                currFm?.dataType
              ),
              mass_total: showTemp(
                convertData(currFm?.massTotal, true),
                currFm?.dataType
              ),
              temperature: showTemp(
                convertData(currFm?.temperature, true),
                currFm?.dataType
              ),
            });
          }
        } else if (logs.ae) {
          const foundKey = Object.keys(logs.ae).find(
            (key) => Number(key) === timestamp
          );
          if (foundKey) {
            const ae = logs.ae[foundKey];
            let currAe;

            if (device.uniqueId.includes('ae-1-1')) {
              currAe = ae.ae1In;
            } else if (device.uniqueId.includes('ae-1-2')) {
              currAe = ae.ae1Out;
            } else if (device.uniqueId.includes('ae-2-1')) {
              currAe = ae.ae2In;
            } else if (device.uniqueId.includes('ae-2-2')) {
              currAe = ae.ae2Out;
            } else if (device.uniqueId.includes('ae-3-1')) {
              currAe = ae.ae3In;
            } else if (device.uniqueId.includes('ae-3-2')) {
              currAe = ae.ae3Out;
            } else if (device.uniqueId.includes('ae-1')) {
              currAe = ae.ae1;
            } else if (device.uniqueId.includes('ae-2')) {
              currAe = ae.ae2;
            } else if (device.uniqueId.includes('ae-3')) {
              currAe = ae.ae3;
            }

            results.push({
              timestamp,
              dateTime: dayjs(timestamp * 1000)
                .utcOffset(timezone * 60)
                .format('DD MMM YYYY HH:mm'),
              volume_flowrate: showTemp(
                convertData(currAe?.volumeFlowrate, true),
                currAe?.dataType
              ),
              volume_inventory: showTemp(
                convertData(currAe?.volumeInventory, true),
                currAe?.dataType
              ),
              volume_total: showTemp(
                convertData(currAe?.volumeTotal, true),
                currAe?.dataType
              ),
              density: showTemp(
                convertData(currAe?.density, true),
                currAe?.dataType
              ),
              mass_flowrate: showTemp(
                convertData(currAe?.massFlowrate, true),
                currAe?.dataType
              ),
              mass_total: showTemp(
                convertData(currAe?.massTotal, true),
                currAe?.dataType
              ),
              mass_inventory: showTemp(
                convertData(currAe?.massInventory, true),
                currAe?.dataType
              ),
              temperature: showTemp(
                convertData(currAe?.temperature, true),
                currAe?.dataType
              ),
              fuel_cons: showTemp(
                convertData(currAe?.stats?.fuelConsVolume, true),
                currAe?.dataType
              ),
              running_time: showTemp(
                convertData(currAe?.runningTime, true),
                currAe?.dataType
              ),
              voltage: showTemp(
                convertData(currAe?.voltage, true),
                currAe?.dataType
              ),
              ampere: showTemp(
                convertData(currAe?.ampere, true),
                currAe?.dataType
              ),
            });
          }
        } else if (logs.gateway) {
          const foundKey = Object.keys(logs.gateway).find(
            (key) => Number(key) === timestamp
          );
          if (foundKey) {
            const gateway = logs.gateway[foundKey];

            results.push({
              timestamp,
              dateTime: dayjs(timestamp * 1000)
                .utcOffset(timezone * 60)
                .format('DD MMM YYYY HH:mm'),
              cpu_usage: showTemp(
                convertData(gateway?.cpuUsage, true),
                gateway?.dataType
              ),
              memory_free: showTemp(
                convertData(gateway?.memoryFree, true),
                gateway?.dataType
              ),
              space_free: showTemp(
                convertData(gateway?.spaceFree, true),
                gateway?.dataType
              ),
              signal_strength: showTemp(
                convertData(gateway?.signalStrength, true),
                gateway?.dataType
              ),
            });
          }
        }
      });

      return results;
    }
  }, [logs, speedUnit, distanceUnit, filter, timezone]);

  const schemaFiltered = useMemo(() => {
    return schema.map((item: any) => {
      if (item.seriesOption.name.toLocaleLowerCase() === 'speed') {
        return {
          ...item,
          seriesOption: {
            ...item.seriesOption,
            tooltip: {
              valueSuffix: speedUnit,
            },
          },
        };
      }

      return { ...item };
    });
  }, [schema, speedUnit, distanceUnit]);

  const initFetch = Boolean(device.deviceId);

  return (
    <Spin spinning={loadingLog}>
      <ContainerFilterDevice>
        <Info>
          <label>Asset</label>
          <div>{device.asset.name}</div>
        </Info>
        <Info>
          <label>Device Unique ID</label>
          <div>{device.uniqueId}</div>
        </Info>
        <FilterReport
          init={initFetch}
          customDevice={device}
          hideFilters={[EFilterItem.company, EFilterItem.asset]}
          variant={isMobile ? 'bottomSheet' : 'default'}
        />
        <ExportDataReport
          columnsFiltered={columns}
          dataSource={dataSource || []}
          loading={false}
        />
      </ContainerFilterDevice>

      <Divider style={{ margin: '15px 0' }} />

      <WrapperTitle style={{ margin: 0 }}>
        <Subtitle>
          Value of line chart with the dotted line is only estimated data
          because the data is not yet available
        </Subtitle>
      </WrapperTitle>

      <ReportChart
        height={height}
        compact={isMobile}
        logs={logs}
        filter={filter}
        yAxis={yAxis}
        chartSchema={schemaFiltered}
      />

      <Table
        className='table-report-log'
        columns={columnFiltered}
        dataSource={dataSource}
        size='small'
        bordered
        pagination={{
          pageSize: isMobile ? 20 : 500,
        }}
        components={isMobile ? undefined : vt}
        scroll={
          isMobile ? { x: 'max-content' } : { x: 700, y: windowHeight - 300 }
        }
      />
    </Spin>
  );
};

const Info = styled.div`
  label {
    font-size: 12px !important;
    color: var(--gray-1) !important;
    font-weight: 600;
    margin-bottom: 10px;
  }

  div {
    height: 32px;
    display: flex;
    align-items: center;
  }
`;

export const ContainerFilterDevice = styled.div`
  padding-top: 10px;
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  gap: 20px;
  align-items: flex-end;

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    padding-top: 0;

    > * {
      min-width: 0;
    }
  }
`;

const WrapperTitle = styled.div`
  margin-bottom: 20px;
  margin-left: 10px;
`;

const Subtitle = styled.div`
  color: #768499;
  font-size: 12;
  margin-top: 5px;

  @media (max-width: 767px) {
    padding: 0 4px;
    line-height: 1.4;
  }
`;

export default DeviceDataLog;
